import { PencilIcon } from "@heroicons/react/solid";
import Button from "components/lib/Shared/Button.jsx";
import DatePickerField from "components/lib/Shared/DatePickerField";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import Input from "components/lib/Shared/Input";
import ValidationError from "components/lib/Shared/ValidationError";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import addressOptions from "components/PLCTABS/SFLdata/addressOptions";
import { Constants } from "config/constants";
import { format, isBefore } from "date-fns";
import { Field, Form, Formik, useFormikContext } from "formik";
import { updateCompanyDashboardState } from "state/slices/companyDashboard";
import { useToast } from "hooks/useToast";
import _get from "lodash/get";
import _omit from "lodash/omit";
import _pick from "lodash/pick";
import _pickBy from "lodash/pickBy";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  createCompany,
  getCompany,
  updateCompanyAsync,
} from "state/slices/company";
import { createRegisteredOfficeAsync } from "state/slices/registeredOffice";
import {
  addBeneficialOwnerAsync,
  getAllBeneficialOwners,
} from "state/slices/tables/beneficialOwnersTableSlice";
import { addCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import { setCreatedCompany } from "state/slices/transferYourBusiness";
import { handleFilesUpload } from "utils/filesUtils";
import { companySummaryValidation } from "./validation";

/**
 * FormEffects lives *inside* <Formik> so it can safely call useFormikContext.
 * All hooks that depend on Formik state are placed here, keeping them at the
 * top level of a React function component and satisfying rules-of-hooks.
 */
const FormEffects = ({ onValidityChange }) => {
  const { values, isValid, setFieldValue } = useFormikContext();

  // Notify parent whenever form validity changes
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  // Keep annualReturnDate in sync with dateOfLastAnnualReturn / registrationDate
  useEffect(() => {
    const isDateInPeriod =
      new Date(values?.dateOfLastAnnualReturn) <= new Date("2016/01/14") &&
      new Date(values?.dateOfLastAnnualReturn) >= new Date("2015/01/01");

    if (isDateInPeriod) {
      setFieldValue("annualReturnDate", values?.dateOfLastAnnualReturn);
    } else {
      setFieldValue("annualReturnDate", values?.registrationDate);
    }
  }, [values?.dateOfLastAnnualReturn, values?.registrationDate]);

  return null;
};

const CompanySummary = ({ partialData, goToNextStep, onValidityChange }) => {
  const {
    companyRegNumber,
    isTransferYourBusinessFromDashboard,
    isTransferYourBusiness,
    transferYourBusinessInitiator,
    transferOrgAdmin,
    createdCompany,
  } = useSelector((state) => state.transferYourBusinessSlice);
  const { companyId, corporateId } = useParams();
  const { company } = useSelector((state) => state.companySlice);
  const { dashboardPermissions, dashboardRoles, companyOfficials } =
    useSelector((state) => state.companyDashboardSlice);

  const dispatch = useDispatch();
  const { toast } = useToast();
  const history = useHistory();

  const [selectedField, setSelectedField] = useState(null);

  let topTenShareholders = partialData?.topTenShareholders || [];
  //Remove shareholderWithBoField from topTenShareholders
  topTenShareholders = topTenShareholders.map((shareholder) => {
    const newOfficial = {
      ...shareholder,
      shareholderWithBO: "",
    };
    return newOfficial;
  });
  const individualOfficials = partialData?.individualOfficials || [];
  const corporateOfficials = partialData?.corporateOfficials || [];
  let beneficialOwners = [];
  if (
    partialData?.beneficialOwners &&
    partialData?.beneficialOwners.length > 0
  ) {
    partialData.beneficialOwners.forEach((owner) => {
      if (
        topTenShareholders.findIndex(
          (shareholder) => shareholder._id === owner._id
        ) === -1
      ) {
        beneficialOwners.push(owner);
      }
    });
  }

  // Remove shareholderWithBO references that do not exist in beneficialOwners if any is linked to partialData._id
  beneficialOwners = beneficialOwners.map((owner) => {
    const newData = { ...owner };
    if (
      owner?.shareholderWithBO &&
      owner?.shareholderWithBO === partialData?._id
    ) {
      newData.shareholderWithBO = null;
    }
    if (
      owner?.shareholderWithBO &&
      !beneficialOwners?.find((bo) => bo?._id === owner?.shareholderWithBO)
    ) {
      newData.shareholderWithBO = null;
    }
    return newData;
  });

  let initialValues = {
    registrationName: "",
    registrationNumber: "",
    type: "",
    entityStatus: Constants.ENTITY_STATUS.DRAFT,
    registrationDate: "",
    accountingEndMonth: "",
    kraPin: "",
    NSSFNo: "",
    NHIFNo: "",
    fileUploads: {
      kraPin: "",
      NSSFNo: "",
      NHIFNo: "",
    },
    resolvedFields: [],
    dateOfLastAnnualReturn: "",
    annualReturnDate: "",
  };

  const contactPerson = {
    fullName: partialData?.identificationDetails?.companyContactName || "",
    emailAddress: partialData?.identificationDetails?.emailAddress || "",
    mobileNumber: partialData?.identificationDetails?.telNumber || "",
    designation:
      partialData?.identificationDetails?.companyContactDesignation || "",
  };

  const registeredOffice = {
    useSimpleFormationsOffice: false,
    // Residential details
    buildingName: partialData?.residentialAddressDetails?.buildingName || "",
    estate: partialData?.residentialAddressDetails?.estate || "",
    houseNumber: partialData?.residentialAddressDetails?.houseNumber || "",
    floorNumber: partialData?.residentialAddressDetails?.floorNumber || "",
    streetName: partialData?.residentialAddressDetails?.streetName || "",
    streetNumber: partialData?.residentialAddressDetails?.streetNumber || "",
    location: {
      county: partialData?.residentialAddressDetails?.county || "",
      district: partialData?.residentialAddressDetails?.district || "",
      locality: partialData?.residentialAddressDetails?.locality || "",
      country: partialData?.residentialAddressDetails?.country || "",
      city: partialData?.residentialAddressDetails?.city || "",
    },

    // Postal details
    postalAddress: partialData?.postalAddress?.postalAddress || "",
    serviceAddress: partialData?.postalAddress?.serviceAddress || "",
    postalCode: partialData?.postalAddress?.postalCode || "",

    // Electronic details
    companyEmailAddress: partialData?.electronicAddress?.emailAddress || "",
    mobileNumber: partialData?.electronicAddress?.mobileNumber || "",
    resolvedFields: [],
  };

  if (companyId && company && Object.values(company).length > 0) {
    initialValues = getCreatedCompanyValues(company);
  } else if (partialData && Object.values(partialData).length > 0) {
    if (partialData?.registrationName) {
      initialValues = getCreatedCompanyValues(partialData);
    } else {
      initialValues = getPartialDataValues(partialData, company);
    }
  }

  if (companyRegNumber) {
    initialValues = {
      ...initialValues,
      registrationNumber: companyRegNumber,
    };
  }

  let initialCorporateData = company
    ? company?.initialCorporateData
    : initialValues;

  initialCorporateData = {
    ...initialValues,
    registeredOffice,
  };

  const getChangedFields = (initialValues, values) => {
    const fieldsToCompare = ["registrationName", "registrationNumber", "type"];

    let initialValuesObj = {
      ..._pick(initialValues, fieldsToCompare),
    };

    const valuesObj = {
      ..._pick(values, fieldsToCompare),
    };

    const changedFields = {};
    Object.keys(initialValuesObj).forEach((key) => {
      if (initialValuesObj[key] !== valuesObj[key]) {
        changedFields[key] = valuesObj[key];
      }
    });
    return changedFields;
  };

  const recreateOfficialData = (official) => {
    const fields = [
      "names",
      "identificationDetails",
      "postalAddress",
      "electronicAddress",
      "residentialAddressDetails",
      "sharesAllotted",
      "beneficialOwnershipForm",
    ];
    // Start from a shallow clone so we never touch the original
    const { _id, __v, createdAt, updatedAt, ...newOfficialBody } = official;
    let newOfficial = { ...newOfficialBody, inviteStatus: "Approved" };

    fields.forEach((field) => {
      const value = official?.[field];

      if (value && Object.values(value).length > 0) {
        const {
          _id,
          __v,
          createdAt,
          updatedAt,
          individualID,
          beneficialOwner,
          corporateID,
          ...rest
        } = value;

        // write onto the clone, not the original
        newOfficial = {
          ...newOfficial,
          [field]: { ...rest },
        };
      }
    });
    return newOfficial;
  };

  const updateBoLinkedToCompany = (primarySourceId, newId) => {
    beneficialOwners = beneficialOwners.map((owner) => {
      if (owner?.linkToCompany?.primarySource === primarySourceId) {
        return {
          ...owner,
          linkToCompany: newId,
        };
      }
      return owner;
    });
    return beneficialOwners;
  };

  const updateShareholderWithBO = (oldId, newId) => {
    beneficialOwners = beneficialOwners.map((owner) => {
      if (owner.shareholderWithBO === oldId) {
        return {
          ...owner,
          shareholderWithBO: newId,
        };
      }
      return owner;
    });
    return beneficialOwners;
  };

  const handleFormOpen = (field) => {
    setSelectedField(field);
  };

  const handleFormClose = () => {
    setSelectedField(null);
  };

  let isBefore2016June15 = false;

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      const changedFields = getChangedFields(initialCorporateData, values);

      let uploadedFiles = {};
      if (values.fileUploads && Object.values(values.fileUploads).length > 0) {
        uploadedFiles = await handleFilesUpload(values.fileUploads);
      }

      if (isBefore2016June15 && !values?.dateOfLastAnnualReturn) {
        toast("error", "Please enter date of last annual return");
        return;
      }

      let companyPayload = {
        ...values,
        transferYourBusinessInitiator,
        fileUploads: uploadedFiles,
        type: values?.type === "PVT" ? "PVT" : "PLC",
        primaryBusinessActivity: {
          accountingEndMonth: values.accountingEndMonth,
        },
        initialCorporateData,
        companyChangedFields: changedFields,
        annualReturnDate: values?.annualReturnDate,
      };
      if (transferOrgAdmin) {
        companyPayload = {
          ...companyPayload,
          orgAdmin: transferOrgAdmin,
        };
      }

      let response = {};
      if (companyId) {
        response = await dispatch(
          updateCompanyAsync({
            id: companyId,
            data: _omit(companyPayload, [
              "transferYourBusinessInitiator",
              "initialCorporateData",
            ]),
          })
        );
        if (response?.payload?.status) {
          toast(
            "success",
            response.payload?.messageText ??
              "Successfully updated the company details"
          );
          dispatch(getCompany({ id: companyId }));
          goToNextStep();
        }
      } else {
        response = await dispatch(
          createCompany({
            data: {
              ...companyPayload,
              corporateTransferRef: corporateId,
              contactPerson,
            },
          })
        );

        if (response?.payload?.status) {
          /**
           *  We do not change the _id fields of the officials coming from partialData. This is to preserve the linkToCompany relationship
           */
          // Create companyOfficials
          if (topTenShareholders.length > 0) {
            for (const shareholder of topTenShareholders) {
              const res = await dispatch(
                addCompanyOfficialAsync({
                  companyId: response.payload.company._id,
                  data: {
                    ...recreateOfficialData(shareholder),
                    designation: shareholder?.role?.join(" "),
                  },
                })
              );

              beneficialOwners = updateShareholderWithBO(
                shareholder?._id,
                res?.payload?.official?._id
              );

              const companyBOsRes = await dispatch(
                getAllBeneficialOwners({
                  companyId: response.payload.company._id,
                })
              );
              const companyBOs = companyBOsRes.payload?.items;
            }
          }

          if (individualOfficials.length > 0) {
            for (const individual of individualOfficials) {
              const res = await dispatch(
                addCompanyOfficialAsync({
                  companyId: response.payload.company._id,
                  data: {
                    ...recreateOfficialData(individual),
                    designation:
                      individual?.role?.join(" ") || individual?.designation,
                  },
                })
              );
              beneficialOwners = updateShareholderWithBO(
                individual?._id,
                res?.payload?.official?._id
              );
            }
          }

          if (corporateOfficials.length > 0) {
            for (const corporate of corporateOfficials) {
              const res = await dispatch(
                addCompanyOfficialAsync({
                  companyId: response.payload.company._id,
                  data: {
                    ...recreateOfficialData(corporate),
                    designation:
                      corporate?.role?.join(" ") || corporate?.designation,
                  },
                })
              );
              beneficialOwners = updateShareholderWithBO(
                corporate?._id,
                res?.payload?.official?._id
              );
            }
          }

          if (beneficialOwners.length > 0) {
            const companyBOsRes = await dispatch(
              getAllBeneficialOwners({
                companyId: response.payload.company._id,
              })
            );
            const companyBOs = companyBOsRes.payload?.items;

            for (const owner of beneficialOwners) {
              if (owner?.linkToCompany) {
                const newBO = companyBOs.find(
                  (bo) =>
                    bo?.primarySource?.toString() ===
                    owner?.linkToCompany?.primarySource?.toString()
                );

                let linkToCompany = null;
                let recreated = recreateOfficialData(owner);

                if (newBO) {
                  linkToCompany = newBO?._id;
                }

                recreated = { ...recreated, linkToCompany };

                await dispatch(
                  addBeneficialOwnerAsync({
                    companyId: response.payload.company._id,
                    data: recreated,
                  })
                );
              } else {
                await dispatch(
                  addBeneficialOwnerAsync({
                    companyId: response.payload.company._id,
                    data: {
                      ...recreateOfficialData(owner),
                      designation: owner?.role?.join(" "),
                    },
                  })
                );
              }
            }
          }

          await dispatch(
            createRegisteredOfficeAsync({
              companyId: response.payload.company._id,
              data: {
                ...registeredOffice,
                companyId: response.payload.company._id,
              },
            })
          );

          dispatch(setCreatedCompany(response.payload.company));
          toast(
            "success",
            response.payload?.messageText ??
              "Successfully created a new company"
          );

          history.push(
            `/companies/${response.payload.company._id}/dashboard?transferYourBusiness`
          );
        }
      }
      if (!response.payload?.status) {
        toast(
          "error",
          response.payload?.messageText ??
            "Something went wrong please try again"
        );
      }

      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
    }
  };

  const handleSaveUploads = async (values) => {
    const uploadedFiles = await handleFilesUpload(values.fileUploads);
    dispatch(
      updateCompanyAsync({
        id: company._id,
        data: {
          ...values,
          fileUploads: {
            ...company.fileUploads,
            ..._pickBy(values.fileUploads, (file) => typeof file === "string"),
            ...uploadedFiles,
          },
        },
      })
    );
    dispatch(
      updateCompanyDashboardState({
        ...values,
        fileUploads: {
          ...company.fileUploads,
          ..._pickBy(values.fileUploads, (file) => typeof file === "string"),
          ...uploadedFiles,
        },
      })
    );
    handleFormClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={() => companySummaryValidation(selectedField)}
      onSubmit={(values, { setSubmitting }) =>
        handleFormSubmit(values, { setSubmitting })
      }
      enableReinitialize
      validateOnMount={true}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({ values, isSubmitting, errors, touched, isValid, setFieldValue }) => {
        // Derive isBefore2016June15 from current form values (no hook needed)
        isBefore2016June15 = isBefore(
          new Date(values.registrationDate),
          new Date("2016/06/15")
        );

        const handleRegistrationDate = (date) => {
          if (!date) {
            setFieldValue("registrationDate", "");
            return;
          }
          if (isNaN(date.getTime())) {
            console.error("Invalid date:", date);
            return;
          }
          const formattedDate = format(date, "yyyy/MM/dd");
          setFieldValue("registrationDate", formattedDate);
        };

        return (
          <Form>
            {/* FormEffects is a proper React component rendered inside <Formik>,
                so its useEffect / useFormikContext calls are always valid. */}
            <FormEffects onValidityChange={onValidityChange} />

            <div className="shadow sm:rounded-md overflow-visible">
              <div className="px-4 py-6 space-y-4 bg-white border-gray-300 sm:p-6 form__wrapper">
                <div className="InputWrapper flex ">
                  <div className="InputList flex flex-col w-full">
                    <div className="flex items-center InputComponent ">
                      <label className="mr-4 text-sm text-gray-600 w-1/4">
                        Registration Name
                      </label>
                      <div className="w-full items-start">
                        <Field
                          name="registrationName"
                          id="registrationName"
                          component={Input}
                          className="flex-grow p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="InputWrapper flex ">
                  <div className="InputList flex flex-col w-full">
                    <div className="flex items-center InputComponent ">
                      <label className="mr-4 text-sm text-gray-600 w-1/4">
                        Registration Number
                      </label>
                      <div className="w-full">
                        <Field
                          name="registrationNumber"
                          id="registrationNumber"
                          component={Input}
                          placeholder="Enter registration number"
                          disabled={companyRegNumber ? true : false}
                          className="flex-grow p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="InputWrapper flex ">
                  <div className="InputList flex flex-col w-full">
                    <div className="flex items-center InputComponent ">
                      <label className="mr-4 text-sm text-gray-600 w-1/4">
                        Entity Type
                      </label>
                      <div className="w-full">
                        <Field
                          name="type"
                          id="type"
                          component={ReactSelectWithFormik}
                          options={Object.entries(
                            Constants.ENTITY_TYPE_AGAINST_TYPE
                          ).map(([value, label]) => ({ value, label }))}
                          onChange={(option) => {
                            setFieldValue("type", option.value);
                          }}
                          placeholder="Enter entity type"
                          className="flex-grow p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                          isDisabled={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <hr />

                <div className="InputWrapper flex ">
                  <div className="InputList flex flex-col w-full">
                    <div className="flex items-center InputComponent ">
                      <label className="mr-4 text-sm text-gray-600 w-1/4">
                        Entity Status
                      </label>
                      <div className="w-full">
                        <Field
                          name="entityStatus"
                          id="entityStatus"
                          component={Input}
                          placeholder="Enter entity status"
                          className="flex-grow p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="InputWrapper flex ">
                  <div className="InputList flex flex-col w-full">
                    <div className="flex items-center InputComponent ">
                      <label className="mr-4 text-sm text-gray-600 w-1/4">
                        Registration Date
                      </label>
                      <div className=" mt-4 w-full">
                        <DatePickerField
                          onChange={handleRegistrationDate}
                          selected={
                            values.registrationDate
                              ? new Date(values.registrationDate)
                              : null
                          }
                          name="registrationDate"
                          placeholderText="Enter registration date"
                          errorPath="registrationDate"
                          disabled={
                            partialData?.names?.dateOfRegistration
                              ? true
                              : false
                          }
                          showRequiredAsterisk
                        />
                        <ValidationError
                          errors={errors}
                          touched={touched}
                          name="registrationDate"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <hr />
                <div className="px-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                  <div className="flex flex-col space-y-2">
                    {[
                      {
                        key: "KRA PIN",
                        name: "kraPin",
                        value:
                          (company?.kraPin ||
                            partialData?.identificationDetails
                              ?.companyKRAPIN) ??
                          "N/A",
                      },
                      {
                        key: "NSSF No.",
                        name: "NSSFNo",
                        value: _get(company, "NSSFNo", "N/A"),
                      },
                      {
                        key: "NHIF No.",
                        name: "NHIFNo",
                        value: _get(company, "NHIFNo", "N/A"),
                      },
                    ].map((field, index) => {
                      const renderCompanyDocumentForm =
                        selectedField && selectedField.name === field.name;

                      return (
                        <React.Fragment key={`${field.key}_${index}`}>
                          <div className="flex flex-col">
                            <div className="flex items-baseline justify-between">
                              <label
                                htmlFor={field.name}
                                className="block mb-2 text-sm text-primary font-medium min-w-[13rem] w-52 whitespace-normal"
                              >
                                {field.key}
                              </label>
                              <div
                                className={`bg-formInput flex $ "w-full"
                                   justify-between relative text-sm text-primary`}
                              >
                                {field.value}
                              </div>
                              {!renderCompanyDocumentForm && (
                                <button
                                  className="flex space-x-1 "
                                  type="button"
                                  onClick={() => {
                                    handleFormOpen(field);
                                  }}
                                >
                                  <span>Upload</span>
                                  <PencilIcon className="inline w-4 h-4" />
                                </button>
                              )}
                            </div>
                            <div
                              className={
                                renderCompanyDocumentForm ? "mt-5 mb-5" : ""
                              }
                            >
                              {renderCompanyDocumentForm && (
                                <div className="flex flex-col space-y-3">
                                  <div className="shadow sm:rounded-md sm:overflow-hidden">
                                    <div className="px-4 py-6 space-y-4 bg-white border-gray-300 sm:p-6">
                                      {field.name === "kraPin" && (
                                        <div className="flex justify-between">
                                          <div className="flex flex-col w-full">
                                            <Field
                                              name="kraPin"
                                              id="kraPin"
                                              placeholder="Add KRA PIN"
                                              component={Input}
                                              className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                                            />
                                            <div className="mt-1">
                                              <label className="block mb-1 text-sm text-gray-600">
                                                KRA PIN Certificate
                                              </label>
                                              <Field
                                                name="fileUploads"
                                                component={FileUploadForm}
                                                wrapperDivStyles="py-2"
                                                documentData={[
                                                  { label: "", name: "kraPin" },
                                                ]}
                                                onlyPreview={false}
                                              />
                                              <ValidationError
                                                errors={errors.fileUploads}
                                                touched={touched.fileUploads}
                                                name={"fileUploads.kraPin"}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      {field.name === "NSSFNo" && (
                                        <div className="flex justify-between">
                                          <div className="flex flex-col w-full">
                                            <Field
                                              name="NSSFNo"
                                              id="NSSFNo"
                                              placeholder="Add NSSF NO"
                                              component={Input}
                                              className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline-none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                                            />
                                            <div className="mt-1">
                                              <label className="block mb-1 text-sm text-gray-600">
                                                NSSF Registration Certificate
                                              </label>
                                              <Field
                                                name="fileUploads"
                                                component={FileUploadForm}
                                                wrapperDivStyles="py-2"
                                                documentData={[
                                                  { label: "", name: "NSSFNo" },
                                                ]}
                                              />
                                              <ValidationError
                                                errors={errors.fileUploads}
                                                touched={touched.fileUploads}
                                                name={"fileUploads.NSSFNo"}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      {field.name === "NHIFNo" && (
                                        <div className="flex justify-between">
                                          <div className="flex flex-col w-full">
                                            <Field
                                              name="NHIFNo"
                                              id="NHIFNo"
                                              placeholder="Add NHIF NO"
                                              component={Input}
                                              className="w-full p-2 text-sm rounded-md bg-formInput text-primary placeholder-primary focus:outline -none focus:ring focus:ring-indigo-100 focus:border-indigo-300"
                                            />
                                            <div className="mt-1">
                                              <label className="block mb-1 text-sm text-gray-600">
                                                NHIF Registration Certificate
                                              </label>
                                              <Field
                                                name="fileUploads"
                                                component={FileUploadForm}
                                                wrapperDivStyles="py-2"
                                                documentData={[
                                                  { label: "", name: "NHIFNo" },
                                                ]}
                                              />
                                              <ValidationError
                                                errors={errors.fileUploads}
                                                touched={touched.fileUploads}
                                                name={"fileUploads.NHIFNo"}
                                              />
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex justify-end space-x-2 py-3">
                                        <Button
                                          disabled={isSubmitting}
                                          className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-500 border border-transparent rounded-md shadow-sm hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed`}
                                          onClick={handleFormClose}
                                        >
                                          <div className="mr-1.5 flex items-center">
                                            Cancel
                                          </div>
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {isBefore2016June15 && (
                  <div className="flex flex-col">
                    <h2 className="text-lg font-sem">
                      Select the date of the last annual return
                    </h2>
                    <label className="mr-4 text-sm text-gray-600 w-1/4">
                      Last Annual Return Date
                    </label>
                    <DatePickerField
                      selected={
                        values?.dateOfLastAnnualReturn
                          ? new Date(values.dateOfLastAnnualReturn)
                          : values?.annualReturnDate
                          ? new Date(values?.annualReturnDate)
                          : null
                      }
                      name="dateOfLastAnnualReturn"
                      disabled={false}
                    />
                  </div>
                )}

                <hr />

                <div className="px-4 space-y-6 bg-white rounded-sm shadow-sm sm:rounded md:rounded-md sm:p-6">
                  <div className="flex justify-between w-full mb-8">
                    <div className="InputWrapper flex w-[50%]">
                      <div className="InputList flex flex-col w-full">
                        <div className="flex items-center InputComponent ">
                          <label className="mr-4 text-sm text-gray-600 w-1/4">
                            Annual Return Date
                          </label>
                          <DatePickerField
                            selected={
                              values?.annualReturnDate
                                ? new Date(values?.annualReturnDate)
                                : null
                            }
                            name="annualReturnDate"
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="InputWrapper flex w-[45%]">
                      <div className="InputList flex flex-col w-full">
                        <div className="flex items-center InputComponent ">
                          <label className="mr-2 text-sm text-gray-600 w-1/4">
                            Financial Year
                          </label>
                          <div className="w-full">
                            <Field
                              name="accountingEndMonth"
                              component={ReactSelectWithFormik}
                              options={addressOptions.months}
                              errorPath="accountingEndMonth"
                              placeholder="Select Month"
                              onChange={(option) => {
                                setFieldValue(
                                  "accountingEndMonth",
                                  option.value
                                );
                              }}
                              isDisabled={false}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ProceedWrapper flex justify-end space-x-2 py-3">
                  <Button
                    type="submit"
                    isLoading={isSubmitting}
                    loadingText="Saving..."
                    className="flex px-2 py-2 text-white button button-hover button-disabled button-outline button-focus bg-sflPrimary"
                  >
                    <div className="ml-1.5 flex items-center">
                      Save and Proceed
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CompanySummary;

function getPartialDataValues(partialData, company) {
  const { names, identificationDetails, fileUploads } = partialData;
  const initialValues = {
    registrationName: names?.companyName,
    registrationNumber: names?.companyRegNumber,
    type:
      identificationDetails?.entityType ===
        Constants.ENTITY_TYPE_AGAINST_TYPE.PVT || company?.type === "PVT"
        ? "PVT"
        : "PLC",
    entityStatus: Constants.ENTITY_STATUS.DRAFT,
    registrationDate: format(new Date(names?.dateOfRegistration), "yyyy/MM/dd"),
    accountingEndMonth: "",
    kraPin: identificationDetails?.companyKRAPIN ?? "",
    NSSFNo: "",
    NHIFNo: "",
    fileUploads: {
      kraPin: fileUploads?.KRACert,
      NSSFNo: "",
      NHIFNo: "",
      articlesOfAssociation: fileUploads?.articlesOfAssociation,
    },
    resolvedFields: [],
  };
  return initialValues;
}

function getCreatedCompanyValues(createdCompany) {
  const {
    registrationName,
    registrationNumber,
    type,
    status,
    registrationDate,
    primaryBusinessActivity,
    kraPin,
    NSSFNo,
    NHIFNo,
    fileUploads,
    annualReturnDate,
  } = createdCompany;

  const initialValues = {
    registrationName,
    registrationNumber,
    type,
    entityStatus: status,
    registrationDate,
    annualReturnDate,
    dateOfLastAnnualReturn: annualReturnDate,
    accountingEndMonth: primaryBusinessActivity?.accountingEndMonth ?? "",
    kraPin,
    NSSFNo,
    NHIFNo,
    fileUploads: {
      kraPin: fileUploads?.kraPin ?? "",
      NSSFNo: fileUploads?.NSSFNo ?? "",
      NHIFNo: fileUploads?.NHIFNo ?? "",
      ...(fileUploads?.articlesOfAssociation && {
        articlesOfAssociation: fileUploads?.articlesOfAssociation,
      }),
      ...createdCompany?.fileUploads,
    },
    resolvedFields: [],
  };
  return initialValues;
}